import React, { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import FormDialog from 'components/form/FormDialog'
import PropTypes from 'prop-types'
import { Col, Row, Table } from 'components/ui'
import { getData } from 'common/utils'
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material'
import { toast } from 'react-toastify'
import Button from 'components/form/Button'
import TextField from '@mui/material/TextField'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { DatePicker } from '@mui/lab'
import { format } from 'date-fns'

const RibbonSharingForm = (props) => {
  const stepLength = 1 // Count From 0
  const { idAdd, isOpen, onClose } = props
  const queryClient = useQueryClient()

  /* ----------------------------------------------- */
  /* ----------------- useState ------------------- */
  /* ----------------------------------------------- */
  const [tenantList, setTenantList] = useState([])
  const [tenantRibbonSharing, setTenantRibbonSharing] = useState([])

  const [step, setStep] = useState(0)
  const [datePickerChange, setDatePickerChange] = useState({})

  /* ----------------------------------------------- */
  /* ----------------- Fetch API ------------------- */
  /* ----------------------------------------------- */
  const { data: tenantListData } = useQuery(['getTenantListShortInfo', idAdd, isOpen], async () => {
    if (idAdd && isOpen) {
      return await getData('GET', `tenants/${localStorage.getItem('tenant')}/tenant-short-info`)
    }
  })

  const mutation = useMutation(async ({ idAdd, dataForm }) => {
    if (idAdd) {
      return await getData('POST', 'share-ribbon', dataForm)
    }
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('getRibbon')
    }
  })

  /* ----------------------------------------------- */
  /* ----------------- useEffect ------------------- */
  /* ----------------------------------------------- */
  useEffect(() => {
    if (tenantListData) {
      const resultDataTenant = tenantListData?.data || []
      if (Array.isArray(resultDataTenant)) {
        const selectData = resultDataTenant.map(item => {
          return {
            value: item.id,
            label: item.name
          }
        })
        setTenantList(selectData)
      }
    }
  }, [tenantListData])

  useEffect(() => {
    if (datePickerChange?.date) {
      tenantRibbonSharing[datePickerChange.index].expireAt = datePickerChange.date
      setDatePickerChange({})
    }
  }, [datePickerChange])

  /* ----------------------------------------------- */
  /* ---------------- handle event ----------------- */
  /* ----------------------------------------------- */
  const handleCloseForm = () => {
    onClose(false)
    setStep(0)
    setTenantRibbonSharing([])
  }

  const handleAddTenantRibbonSharing = (value) => () => {
    const currentIndex = tenantRibbonSharing.findIndex(item => item.tenant.value === value.value)
    const newChecked = [...tenantRibbonSharing]

    if (currentIndex === -1) {
      newChecked.push({
        tenant: value,
        range: 0,
        expireAt: format(new Date(), 'yyyy-MM-dd')
      })
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setTenantRibbonSharing(newChecked)
  }

  const handleNextStep = () => {
    if (step === stepLength) {
      setStep(step - 1)
    } else {
      setStep(step + 1)
    }
  }

  const handleChangeDatePicker = (e, index) => {
    const date = format(new Date(e), 'yyyy-MM-dd')
    setDatePickerChange({ index: index, date: date })
  }

  const handleChangeDayRange = (e, index) => {
    const newTenantShared = [...tenantRibbonSharing]
    newTenantShared[index].range = e?.target?.value || 0
    setTenantRibbonSharing(newTenantShared)
  }

  const onSubmit = () => {
    const tenants = tenantRibbonSharing.map(item => {
      return { tenant_id: item.tenant.value, expire_at: item.expireAt, range: item?.range || 0 }
    })
    if (tenants.length > 0) {
      const dataForm = {
        owner: localStorage.getItem('tenant'),
        ribbon: idAdd,
        tenants: tenants
      }
      try {
        mutation.mutateAsync({ idAdd, dataForm }).then(() => {
          toast.success('Ribbon Sharing Successful!')
          handleCloseForm()
          setStep(0)
          setTenantRibbonSharing([])
        })
      } catch (error) {
        toast.error('Something went wrong!')
      }
    }
  }

  const columns = [
    {
      title: 'Tenant',
      key: 'tenant',
      align: 'left',
      width: '30%',
      ellipsis: true,
      // eslint-disable-next-line react/display-name
      render: (text, record, index) => {
        return (
          <div key={index}>
            { record?.tenant?.label || '' }
          </div>
        )
      }
    },
    {
      title: 'Day(s) Range',
      key: 'day_range',
      align: 'left',
      width: '12%',
      ellipsis: true,
      // eslint-disable-next-line react/display-name
      render: (text, record, index) => {
        return (
          <div key={index}>
            <TextField
              id='range-expire'
              label='Day(s) Range'
              type='number'
              InputProps={{ inputProps: { min: 0 } }}
              defaultValue={0}
              onBlur={(e) => handleChangeDayRange(e, index)}
              />
          </div>
        )
      }
    },
    {
      title: 'Expire At',
      key: 'expire_at',
      align: 'left',
      width: '20%',
      // eslint-disable-next-line react/display-name
      render: (text, record, index) => {
        return (
          <div key={index}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                disabled={false}
                label={'Expire at'}
                ampm={false}
                inputFormat='dd-MM-yyyy'
                mask="__-__-____"
                value={record?.expireAt}
                onChange={(e) => handleChangeDatePicker(e, index)}
                renderInput={(params) => <TextField {...params} size={'medium'} fullWidth />}
              />
            </LocalizationProvider>
          </div>
        )
      }
    }
  ]

  return (
    <div className='vod-ribbon-sharing-form'>
      <FormDialog
        title='Ribbon Sharing For Tenants'
        maxWidth="md"
        isOpen={isOpen}
        dividers
        onClose={handleCloseForm}
        onSave={() => onSubmit()}
        customButton={
          <Button variant="contained" color="success" className="ml-2" onClick={handleNextStep}>
            { step >= stepLength ? 'Back' : 'Next' }
          </Button>
        }
      >
        {step === 0 && (
          <Row>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {
                tenantList.map((t, key) => {
                  const labelId = `checkbox-list-label-${t.value}`
                  const checked = tenantRibbonSharing.find(item => item.tenant.value === t.value)
                  return (
                    <ListItem
                      key={key}
                      disablePadding
                    >
                      <ListItemButton onClick={handleAddTenantRibbonSharing(t)} dense>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={!!checked}
                            tabIndex={-1}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={`${t.label}`} />
                      </ListItemButton>
                    </ListItem>
                  )
                })
              }
            </List>
          </Row>
        )}
        {
          step === 1 && (
            <Row className="pt-4">
              <Col md={12}>
                <Table
                  scroll={{
                    x: 800
                  }}
                  columns={columns}
                  data={tenantRibbonSharing}
                />
              </Col>
            </Row>
          )
        }
      </FormDialog>
    </div>
  )
}

RibbonSharingForm.propTypes = {
  idAdd: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
}

RibbonSharingForm.defaultProps = {
  idAdd: '',
  isOpen: false
}

export default RibbonSharingForm
